import React from 'react';

const FloatingField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="floating-field">
    <span className="floating-label">{label}</span>
    {children}
  </div>
);

const RegistrationSection = ({ t, props }: any) => {
  if (!props || !props.values) return null;

  const { races, racesLoading, raceTypes, selectedRace, registrationOpen } = props;

  return (
    <section id="register" className="registration-section">
      <h2>{t.raceRegistration}</h2>

      {racesLoading && <p style={{ textAlign: 'center' }}>Loading races...</p>}

      {!racesLoading && races && races.length === 0 && (
        <p style={{ textAlign: 'center' }}>No races available at this time.</p>
      )}

      {!racesLoading && races && races.length > 0 && (
        <form className="registration-form" onSubmit={props.onSubmit}>
          {/* Registration date info */}
          {selectedRace && (
            <div style={{ textAlign: 'center', fontSize: '0.9rem', color: registrationOpen ? '#2a7d2e' : '#c00', marginBottom: 8 }}>
              {selectedRace.openRegistrationDate && (
                <span>{t.reg.opensOn || 'Opens'}: {new Date(selectedRace.openRegistrationDate).toLocaleDateString()} </span>
              )}
              {selectedRace.closeRegistrationDate && (
                <span> | {t.reg.closesOn || 'Closes'}: {new Date(selectedRace.closeRegistrationDate).toLocaleDateString()}</span>
              )}
              {!registrationOpen && (
                <div style={{ fontWeight: 700, marginTop: 4 }}>{t.reg.registrationClosed || 'Registration is currently closed for this race.'}</div>
              )}
            </div>
          )}

          {/* Race Type Selection */}
          <div className="form-row">
            <FloatingField label={t.reg.selectRaceType || 'Race type'}>
              <select name="raceTypeId" value={props.values.raceTypeId} onChange={props.onChange} required disabled={!props.values.raceId}>
                <option value="">&nbsp;</option>
                {raceTypes.map((rt: any) => (
                  <option key={rt.id} value={rt.id}>
                    {rt.name} ({rt.distance} km) — {rt.price} MKD
                  </option>
                ))}
              </select>
            </FloatingField>
          </div>

          {/* Personal Information */}
          <div className="form-row">
            <FloatingField label={t.reg.firstName}>
              <input type="text" name="firstName" value={props.values.firstName} onChange={props.onChange} required />
            </FloatingField>
            <FloatingField label={t.reg.lastName}>
              <input type="text" name="lastName" value={props.values.lastName} onChange={props.onChange} required />
            </FloatingField>
          </div>
          <div className="form-row">
            <FloatingField label={t.reg.email}>
              <input type="email" name="email" value={props.values.email} onChange={props.onChange} required />
            </FloatingField>
            <FloatingField label={t.reg.phone}>
              <input type="tel" name="phone" value={props.values.phone} onChange={props.onChange} required />
            </FloatingField>
          </div>
          <div className="form-row">
            <FloatingField label={t.reg.city}>
              <input type="text" name="city" value={props.values.city} onChange={props.onChange} required />
            </FloatingField>
            <FloatingField label={t.reg.country}>
              <input type="text" name="country" value={props.values.country} onChange={props.onChange} required />
            </FloatingField>
          </div>
          <div className="form-row">
            <FloatingField label={t.reg.gender}>
              <select name="gender" value={props.values.gender} onChange={props.onChange} required>
                <option value="">&nbsp;</option>
                <option value="Male">{t.reg.genderMale}</option>
                <option value="Female">{t.reg.genderFemale}</option>
                <option value="Other">{t.reg.genderOther}</option>
              </select>
            </FloatingField>
            <FloatingField label={t.reg.dob}>
              <input type="date" name="dateOfBirth" value={props.values.dateOfBirth} onChange={props.onChange} required />
            </FloatingField>
          </div>

          {/* Participation Details */}
          <div className="form-row">
            <FloatingField label={t.reg.participantType || 'Participant type'}>
              <select name="participantType" value={props.values.participantType} onChange={props.onChange} required>
                <option value="Individual">{t.reg.individual}</option>
                <option value="Team">{t.reg.team}</option>
              </select>
            </FloatingField>
            {props.values.participantType === 'Team' && (
              <FloatingField label={t.reg.teamName}>
                <input type="text" name="teamName" value={props.values.teamName} onChange={props.onChange} />
              </FloatingField>
            )}
          </div>
          <div className="form-row">
            <FloatingField label={t.reg.tshirtSize || 'T-Shirt Size'}>
              <select name="tshirtSize" value={props.values.tshirtSize} onChange={props.onChange} required disabled={!props.tshirtSizes || props.tshirtSizes.length === 0}>
                {props.tshirtSizes && props.tshirtSizes.length > 0
                  ? props.tshirtSizes.map((size: string) => (
                      <option key={size} value={size}>{size}</option>
                    ))
                  : <option value="">&nbsp;</option>
                }
              </select>
            </FloatingField>
            <FloatingField label={t.reg.discountCode}>
              <input type="text" name="discountCode" value={props.values.discountCode} onChange={props.onChange} />
            </FloatingField>
          </div>
          <div className="form-row">
            <FloatingField label={t.reg.emergencyContactPhone || 'Emergency Contact Phone'}>
              <input type="tel" name="emergencyContactPhone" value={props.values.emergencyContactPhone} onChange={props.onChange} required />
            </FloatingField>
          </div>

          {/* Legal */}
          <div className="form-row checkboxes">
            <label className={`toggle-label${!props.values.cookiesConsent && props.error ? ' toggle-error' : ''}`}>
              <input
                type="checkbox"
                name="cookiesConsent"
                checked={props.values.cookiesConsent}
                onChange={props.onCheckboxChange}
                className="toggle-input"
              />
              <span className="toggle-slider" />
              <span className={props.values.cookiesConsent ? 'checked' : ''}>
                {t.reg.cookiesConsent || 'I agree to cookies'}
              </span>
            </label>
          </div>
          <div className="form-row checkboxes">
            <label className={`toggle-label${!props.values.privacyPolicy && props.error ? ' toggle-error' : ''}`}>
              <input
                type="checkbox"
                name="privacyPolicy"
                checked={props.values.privacyPolicy}
                onChange={props.onCheckboxChange}
                className="toggle-input"
              />
              <span className="toggle-slider" />
              <span
                className={props.values.privacyPolicy ? 'checked' : ''}
                style={{ color: !props.values.privacyPolicy && props.error ? '#c00' : undefined }}
              >
                {t.reg.privacyPolicy}
              </span>
            </label>
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ marginLeft: 16, color: '#1e6ad6', textDecoration: 'underline', fontWeight: 600 }}>
              {t.readPrivacyPolicy}
            </a>
          </div>
          {props.error && <div className="form-error">{props.error}</div>}
          <button type="submit" className="btn primary" disabled={props.submitting || !registrationOpen}>
            {props.submitting ? t.reg.submitting : t.reg.submit}
          </button>
        </form>
      )}
    </section>
  );
};

export default RegistrationSection;
